import type { BookkeepingTag, BookkeepingTagCategory } from '@components'

import type { SelectOption } from '@shinetools/lumen-react'
import uniqBy from 'lodash/uniqBy'

interface DescriptionCandidate {
  accountId: string
  name: string
}

// BookkeepingTag is the only shape with an accountId; a BookkeepingTagCategory's id is its own identity.
const getAccountId = (category: BookkeepingTag | BookkeepingTagCategory): string =>
  'accountId' in category ? category.accountId : String(category.id)

export const getPurchaseDescriptionOptions = (
  categories: (BookkeepingTag | BookkeepingTagCategory)[],
  selectedDescription?: string,
): SelectOption[] => {
  // The same name can sit under several accounts (confirmed with bookkeeping) - deduplicating by
  // name would silently drop a distinct entry because another one happens to share its name. So
  // entries are deduplicated by account instead, even though that means two entries can still look
  // identical in the list - that's a known limitation pending a product/design decision.
  const candidates: DescriptionCandidate[] = categories.flatMap((category) => {
    if ('question' in category && category.question.answers.length) {
      return category.question.answers.map((answer) => ({ accountId: answer.accountId, name: answer.name }))
    }

    return [{ accountId: getAccountId(category), name: category.name }]
  })

  const options = uniqBy(candidates, 'accountId')
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(({ name }) => ({ label: name, value: name }))

  if (selectedDescription && !options.some((option) => option.value === selectedDescription)) {
    return [{ label: selectedDescription, value: selectedDescription }, ...options]
  }

  return options
}
